import React, { useEffect } from "react";
import { MdCall, MdEmail } from "react-icons/md";
import PagesTitleCol from "../components/cols/PagesTitleCol";
import Body from "../components/Wrapper/Body";
import classes from "../Styles/Contact.module.css";
import classesColStyles from "../components/cols/ColsStyles.module.css";
import { FaMapMarker } from "react-icons/fa";
import NewsLetter from "../components/cols/NewsLetter";
import Faq from "../components/FAQ/Faq";
import { Helmet } from "react-helmet";
import { Formik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useUploadFileMutation } from "../store/services/users";

const SITE_URL = "https://sprinterwave.com";
const PAGE_URL = `${SITE_URL}/contact`;
const PAGE_TITLE = "Contact Sprinterwave — Talk to Our Support Team";
const PAGE_DESCRIPTION =
  "Need to get in touch? Reach Sprinterwave by phone, email, or our contact form for support, feedback, or general enquiries. Our team responds fast.";

function ContactUs() {
  const [PostRequest, { isLoading }] = useUploadFileMutation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const contactSchema = Yup.object().shape({
    firstname: Yup.string()
      .min(2, "Too short!")
      .max(50, "Too long!")
      .required("Please enter your first name"),
    lastname: Yup.string()
      .min(2, "Too short!")
      .max(50, "Too long!")
      .required("Please enter your last name"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Your valid email is required"),
    subject: Yup.string()
      .min(2, "Too short!")
      .max(190, "Too long!")
      .required("Subject is required"),
    message: Yup.string()
      .min(2, "Too short!")
      .max(1000, "Too long!")
      .required("Message is required"),
  });

  // Structured data: lets search engines surface our phone/email/address
  // directly, and ties this page to the Sprinterwave organization entity.
  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: PAGE_TITLE,
    url: PAGE_URL,
    description: PAGE_DESCRIPTION,
    mainEntity: {
      "@type": "Organization",
      name: "Sprinterwave",
      url: SITE_URL,
      email: "sprinterwave@gmail.com",
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+234-808-002-5944",
          email: "sprinterwave@gmail.com",
          contactType: "customer service",
          areaServed: "NG",
          availableLanguage: ["English"],
        },
      ],
      address: {
        "@type": "PostalAddress",
        addressLocality: "Port Harcourt",
        addressCountry: "NG",
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Contact", item: PAGE_URL },
    ],
  };

  return (
    <Body>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <meta charSet="utf-8" />
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content="Contact Sprinterwave, customer support, bill payment help, Sprinterwave phone number, Sprinterwave email"
        />

        <link rel="canonical" href={PAGE_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Sprinterwave" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta
          property="og:image"
          content={`${SITE_URL}/images/og/contact-cover.jpg`}
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta
          name="twitter:image"
          content={`${SITE_URL}/images/og/contact-cover.jpg`}
        />

        <script type="application/ld+json">
          {JSON.stringify(contactPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <PagesTitleCol
        description="Contact us anytime"
        title="Contact Us"
        bg_imageName="contact"
      />

      <main className={classes.main}>
        <div className={classes.left_container}>
          <div className={classesColStyles.data} id="contact_us">
            <p className={classes.eyebrow}>Get in touch</p>
            <h2>Contact Information</h2>

            <p>
              Need to get in touch? We're here for you! Reach us easily
              through our contact details for any queries or assistance.
              Your feedback matters, and we're ready to assist.
            </p>

            <div
              className={`${classesColStyles.flex_btm_box} ${classesColStyles.col_formart}`}
            >
              <div className={classesColStyles.row_format}>
                <div className={classesColStyles.iconChip} data-tone="green">
                  <MdCall aria-hidden="true" />
                </div>
                <div>
                  <h3>Call our support center</h3>
                  <p>
                    <a href="tel:+2348080025944">+234 808 002 5944</a>
                  </p>
                </div>
              </div>

              <div className={classesColStyles.row_format}>
                <div className={classesColStyles.iconChip} data-tone="blue">
                  <MdEmail aria-hidden="true" />
                </div>
                <div>
                  <h3>Email us</h3>
                  <p>
                    <a href="mailto:sprinterwave@gmail.com">
                      sprinterwave@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className={classesColStyles.row_format}>
                <div className={classesColStyles.iconChip} data-tone="purple">
                  <FaMapMarker aria-hidden="true" />
                </div>
                <div>
                  <h3>HQ location</h3>
                  <p>Port Harcourt, Nigeria</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.right_container}>
          <p className={classes.eyebrow}>Send a message</p>
          <h2>Send Us Your Query Here</h2>
          <p>Let us know how to get back to you.</p>

          <Formik
            initialValues={{
              firstname: "",
              lastname: "",
              subject: "",
              email: "",
              message: "",
            }}
            onSubmit={(values, { resetForm }) => {
              PostRequest({
                url: "/contact-us",
                body: {
                  ...values,
                },
              })
                .unwrap()
                .then(() => {
                  Swal.fire(
                    "Message received!",
                    "Your message has been submitted. We'll get back to you at the email address you provided as soon as we can.",
                    "success"
                  );
                  resetForm();
                })
                .catch((error) => {
                  console.log(error?.data?.message);
                  Swal.fire(
                    error?.data?.message || "Something went wrong. Please try again."
                  );
                });
            }}
            validationSchema={contactSchema}
          >
            {({
              handleChange,
              handleSubmit,
              touched,
              values,
              errors,
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <div className={classes.form_wrapper}>
                  <div className={classes.input_wrapper}>
                    <label htmlFor="firstname">First Name *</label>
                    <input
                      type="text"
                      name="firstname"
                      id="firstname"
                      autoComplete="given-name"
                      placeholder="First name"
                      value={values.firstname}
                      onChange={handleChange("firstname")}
                    />
                    {touched.firstname && errors.firstname && (
                      <p className={classes.error}>{errors.firstname}</p>
                    )}
                  </div>
                  <div className={classes.input_wrapper}>
                    <label htmlFor="lastname">Last Name *</label>
                    <input
                      type="text"
                      name="lastname"
                      id="lastname"
                      autoComplete="family-name"
                      placeholder="Last name"
                      value={values.lastname}
                      onChange={handleChange("lastname")}
                    />
                    {touched.lastname && errors.lastname && (
                      <p className={classes.error}>{errors.lastname}</p>
                    )}
                  </div>
                </div>

                <div className={classes.form_wrapper}>
                  <div className={classes.input_wrapper}>
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      autoComplete="email"
                      placeholder="example@mail.com"
                      value={values.email}
                      onChange={handleChange("email")}
                    />
                    {touched.email && errors.email && (
                      <p className={classes.error}>{errors.email}</p>
                    )}
                  </div>
                  <div className={classes.input_wrapper}>
                    <label htmlFor="subject">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      autoComplete="off"
                      placeholder="Issue here"
                      value={values.subject}
                      onChange={handleChange("subject")}
                    />
                    {touched.subject && errors.subject && (
                      <p className={classes.error}>{errors.subject}</p>
                    )}
                  </div>
                </div>

                <div className={classes.text_area_section}>
                  <h2>How Can We Help?</h2>
                  <p>Feel free to ask a question or simply leave a comment</p>

                  <label htmlFor="message">Comments / Questions *</label>
                  <textarea
                    name="message"
                    id="message"
                    placeholder="Add text"
                    value={values.message}
                    onChange={handleChange("message")}
                  ></textarea>
                  {touched.message && errors.message && (
                    <p className={classes.error}>{errors.message}</p>
                  )}

                  <button type="submit" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </main>

      <br />
      <Faq />

      {/* <NewsLetter /> */}
    </Body>
  );
}

export default ContactUs;